import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import { StaticTableFilterHook } from "commons/hooks/useStaticTableFilter";
import useStaticTableFilterWithMemory from "commons/hooks/useStaticTableFilterWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import { Column, FilterFieldCollection, FilterOperation, FilterType } from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";

import { makeUrl } from "commons/utils/http";

import useDocumentApi from "hooks/api/private/useDocumentApi";
import useSnackbar from "hooks/useSnackbar";

import { DocumentListItem } from "types/api/document";

export type DocumentsListHook = {
    navigateToNewDocument: () => void;
    onSearchFilterChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    dataTableHook: DataTableHook<DocumentListItem>;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    staticFilterHook: StaticTableFilterHook;
    alertConfirmHook: AlertConfirmHookType;
};

function useDocumentsList(): DocumentsListHook {
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    title: {
                        key: "title",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        [],
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "documents-list-search",
        memoizedFieldsForSearch,
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);
                newForm.search.title = "" + event.target.value;
                return newForm;
            }),
        [setFiltersForm],
    );

    const memoizedFieldsForStaticFilters = React.useMemo(
        () => ({
            language: {
                key: "language",
                operation: FilterOperation.Eq,
                defaultValue: "it",
            },
            enabled: {
                key: "enabled",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
        }),
        [],
    );

    const staticFilterHook = useStaticTableFilterWithMemory(
        "documents-list-static",
        memoizedFieldsForStaticFilters,
    );

    const { getAllDocuments, deleteDocument } = useDocumentApi();

    const navigate = useNavigate();

    const navigateToEditDocument = React.useCallback(
        (row: DocumentListItem) => {
            navigate(makeUrl(`${row.id}/modifica`));
        },
        [navigate],
    );

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina documento",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteDocumentButton = React.useCallback(
        (row: DocumentListItem) => {
            openConfirmAlert("Sei sicuro di voler eliminare il documento?", () => {
                const [apiCall] = deleteDocument({ id: row.id });

                apiCall
                    .then(() => {
                        setTriggerReloadTable(true);
                        enqueueSuccessSnackbar("Documento eliminato con successo");
                    })
                    .catch(() => enqueueErrorSnackbar("Errore nell'eliminazione del documento"));
            });
        },
        [enqueueSuccessSnackbar, setTriggerReloadTable, enqueueErrorSnackbar, openConfirmAlert, deleteDocument],
    );

    const columns: Array<Column<DocumentListItem>> = React.useMemo(
        () => [
            {
                id: "title",
                header: "Titolo",
                render: (row: DocumentListItem) => row.title,
            },
            {
                id: "attachment_filename",
                header: "Allegato",
                render: (row: DocumentListItem) => row.attachment_filename ?? "-",
            },
            {
                id: "enabled",
                header: "Visibile",
                width: "130px",
                render: (row: DocumentListItem) => (row.enabled ? "Visibile" : "Non visibile"),
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: DocumentListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={navigateToEditDocument}
                        onDelete={deleteDocumentButton}
                    />
                ),
            },
        ],
        [navigateToEditDocument, deleteDocumentButton],
    );

    const { filters: searchFilters } = filterSearchHook;
    const { filters: staticFilters } = staticFilterHook;

    const filters: FilterType[] = React.useMemo(
        () => [...searchFilters, ...staticFilters],
        [searchFilters, staticFilters],
    );

    const dataTableHook = useDataTableWithMemory<DocumentListItem>({
        apiCall: getAllDocuments,
        getElements: calculateRows,
        columns: columns,
        filters: filters,
        size: 20,
        key: "documents-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();
            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    const navigateToNewDocument = React.useCallback(() => {
        navigate("/cm-documenti/nuovo");
    }, [navigate]);

    return React.useMemo(
        (): DocumentsListHook => ({
            onSearchFilterChange,
            navigateToNewDocument,
            filterSearchHook,
            staticFilterHook,
            alertConfirmHook,
            dataTableHook,
        }),
        [onSearchFilterChange, navigateToNewDocument, filterSearchHook, alertConfirmHook, staticFilterHook, dataTableHook],
    );
}

export default useDocumentsList;
